import { Box, Card, CardContent, CardHeader, LinearProgress, Stack, Typography } from '@mui/material';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import type { WeeklySummary, ClaudeLimits } from '../../api/claude';

function fmtK(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

function usageColor(pct: number): 'success' | 'warning' | 'error' {
  if (pct >= 90) return 'error';
  if (pct >= 70) return 'warning';
  return 'success';
}

interface Props {
  weekly: WeeklySummary;
  limits: ClaudeLimits;
}

export function TokenUsageCard({ weekly, limits }: Props) {
  const pct = Math.min(weekly.percent_of_weekly_limit, 100);
  const color = usageColor(weekly.percent_of_weekly_limit);
  const windowStart = new Date(weekly.window_start).toLocaleDateString();

  return (
    <Card>
      <CardHeader
        avatar={<ShowChartIcon color="primary" />}
        title="Weekly Token Budget"
        titleTypographyProps={{ variant: 'h6' }}
        subheader={`Rolling 7-day window from ${windowStart}`}
      />
      <CardContent>
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {fmtK(weekly.total_tokens)} / {fmtK(limits.weekly_token_limit)} tokens
          </Typography>
          <Typography variant="body2" fontWeight={500} color={`${color}.main`}>
            {weekly.percent_of_weekly_limit.toFixed(1)}%
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={pct}
          color={color}
          sx={{ height: 8, borderRadius: 1, mb: 1.5 }}
        />
        <Stack direction="row" spacing={3}>
          <Box>
            <Typography variant="caption" color="text.secondary">Input</Typography>
            <Typography variant="body2">{fmtK(weekly.input_tokens)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Output</Typography>
            <Typography variant="body2">{fmtK(weekly.output_tokens)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Session limit</Typography>
            <Typography variant="body2">{fmtK(limits.session_token_limit)}</Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
